import { getBootProcessing, getRecommendStore, getUnConfigBootStrapStoreInfo } from '../service';
import { useMemoizedFn } from 'ahooks';
import { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { GetUnConfigBootStrapStoreInfoReturn, PostBootProcessingData } from '../types/api';
import type { ReuseStoreInfo } from '../types/reuse';
import useLLM from './useLLM';

export function initProgressBodyBasic(
  stores: GetUnConfigBootStrapStoreInfoReturn,
): PostBootProcessingData {
  return {
    stepName: 'WELCOME_MESSAGE',
    configType: 'BASIC',
    content: '',
    status: 'IN_PROGRESS',
    storeIdList: stores.map((item) => item.storeId),
  };
}
export function initProgressBodyAiModel(
  stores: GetUnConfigBootStrapStoreInfoReturn,
): PostBootProcessingData {
  return {
    stepName: 'AI_MODEL_LOGISTICS',
    configType: 'AI_MODEL',
    content: '',
    status: 'IN_PROGRESS',
    storeIdList: stores.map((item) => item.storeId),
  };
}
export function initProgressBodyAdvance(
  stores: GetUnConfigBootStrapStoreInfoReturn,
): PostBootProcessingData {
  return {
    stepName: 'LOGISTICS_CARE',
    configType: 'ADVANCED',
    content: '',
    status: 'IN_PROGRESS',
    storeIdList: stores.map((item) => item.storeId),
  };
}
export const firstPageBasic = `/bootguide/basic/WELCOME_MESSAGE`;
export const firstPageAi = '/bootguide/ai_model/AI_MODEL_LOGISTICS';
export const firstPageAdvance = `/bootguide/advanced/LOGISTICS_CARE`;

const reuseEnable = false;

export enum HomePageType {
  // 基础配置引导
  BasicStart = 1,
  // Ai
  AiModelStart,
  // 进阶配置引导首
  AdvancedStart,
  // 继续配置引导
  Continue,
}

export const useInitial = () => {
  const [loading, setLoading] = useState(true);
  const [isInitial, setIsInitial] = useState(false);
  const [next, setNext] = useState('');
  const [state, setState] = useState<
    ReuseStoreInfo & { basicUnConfigStores?: ReuseStoreInfo['unconfigStores'] }
  >(null!);
  const [homePageType, setHomePageType] = useState<HomePageType>(HomePageType.BasicStart);

  const LLM = useLLM();

  const setup = useMemoizedFn(async () => {
    setLoading(true);
    try {
      const [
        basicUnConfigStores,
        advanceUnConfigStores,
        aiModelUnConfigStores,
        inProgress,
        recommendStore,
      ] = await Promise.all([
        getUnConfigBootStrapStoreInfo({ type: 1 }),
        getUnConfigBootStrapStoreInfo({ type: 2 }),
        LLM ? getUnConfigBootStrapStoreInfo({ type: 3 }) : Promise.resolve([]),
        getBootProcessing(LLM),
        reuseEnable ? getRecommendStore() : Promise.resolve([]),
      ]);

      const basicInProgress = inProgress.find((item) => item.configType === 'BASIC');
      const aiModelInProgress = inProgress.find((item) => item.configType === 'AI_MODEL');
      const advanceInProgress = inProgress.find((item) => item.configType === 'ADVANCED');

      let next = '';
      let state: ReuseStoreInfo & {
        start?: () => Promise<any>;
        basicUnConfigStores?: ReuseStoreInfo['unconfigStores'];
      } = null!;
      let type: HomePageType;

      const hasBasic = basicUnConfigStores.length > 0;
      const hasAiModel = aiModelUnConfigStores.length > 0;
      const hasAdvance = advanceUnConfigStores.length > 0;
      const isBasicInProgress = !!basicInProgress;
      const isAiModelInProgress = !!aiModelInProgress;
      const isAdvanceInProgress = !!advanceInProgress;

      if (isBasicInProgress) {
        next = `/bootguide/basic/${basicInProgress!.stepName}`;
        type = HomePageType.Continue;
        state = {
          unconfigStores: basicUnConfigStores,
          recommendStore,
        };
      } else if (isAiModelInProgress) {
        next = `/bootguide/ai_model/${aiModelInProgress!.stepName}`;
        type = HomePageType.Continue;
        state = {
          unconfigStores: aiModelUnConfigStores,
          recommendStore,
        };
      } else if (isAdvanceInProgress) {
        next = `/bootguide/advanced/${advanceInProgress!.stepName}`;
        type = HomePageType.Continue;
        state = {
          unconfigStores: advanceUnConfigStores,
          recommendStore,
        };
      } else if (hasBasic) {
        type = HomePageType.BasicStart;
        next = firstPageBasic;
        state = {
          unconfigStores: basicUnConfigStores,
          recommendStore,
        };
      } else if (hasAiModel) {
        type = HomePageType.AiModelStart;
        next = firstPageAi;
        state = {
          unconfigStores: aiModelUnConfigStores,
          recommendStore,
          basicUnConfigStores,
        };
      } else if (hasAdvance) {
        type = HomePageType.AdvancedStart;
        next = firstPageAdvance;
        state = {
          unconfigStores: advanceUnConfigStores,
          recommendStore,
          basicUnConfigStores,
        };
      }

      unstable_batchedUpdates(() => {
        setNext(next);
        setState(state);
        setIsInitial(inProgress.length !== 0);
        setHomePageType(type);
      });
    } catch (err) {
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    setup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    loading,
    isInitial,
    next,
    state,
    homePageType,
  };
};
